import axios from 'axios'
import Vue from 'vue'
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import moment from 'moment'
import Form from '../components/PeerTubeForm.vue'
import get from 'lodash/get'
import {getAbsoluteUrl} from "@/utils"

export const SCOPES = "user"

export async function createApp(ax) {
  return await ax.get('/api/v1/oauth-clients/local')
}

function getAuthorizationHeader (account, state) {
  const token = state.sources.peertube.appTokens[`${account.username}@${account.domain}`].access_token
  return `Bearer ${token}`

}

export function getOauthAxios ({axiosParams, account, store}) {
  // an axios instance with autorefreshing for oauth tokens
  let client = axios.create(axiosParams || {})
  client.interceptors.request.use(request => {
    request.headers['Authorization'] = getAuthorizationHeader(account, store.state)
    return request
  })
  createAuthRefreshInterceptor(client, (failedRequest) => {
    console.log('Request failed, OAuth token probably expired, fetching a new one…')
    const refreshToken = store.state.sources.peertube.appTokens[`${account.username}@${account.domain}`].refresh_token
    const app = store.state.sources.peertube.appCredentials[account.domain]
    const payload = {
      client_id: app.client_id,
      client_secret: app.client_secret,
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    }
    return client.post(
      `/api/v1/users/token`,
      asForm(payload),
      {headers: {'Content-Type': 'multipart/form-data'}}
    ).then((tokenRefreshResponse) => {
      store.commit('setRecursiveState', {key: "sources.peertube.appTokens", suffix: `${account.username}@${account.domain}`, value: tokenRefreshResponse.data})
      let newHeader = getAuthorizationHeader(account, store.state)
      failedRequest.response.config.headers['Authorization'] = newHeader
      return Promise.resolve()
    })
  })
  return client
}

function asForm (obj) {
  let data = new FormData()
  Object.entries(obj).forEach((e) => {
    data.set(e[0], e[1])
  })
  return data
}

function objectToQs (obj) {
  let str = [];
  for (let p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
}

export async function connect({domain, router, store, username, password}) {
  console.log(`Received connect callback for peertube domain ${domain}`)
  console.log('Fetching token...')
  const app = store.state.sources.peertube.appCredentials[domain]
  const ax = axios.create()
  const payload = {
    client_id: app.client_id,
    client_secret: app.client_secret,
    grant_type: "password",
    response_type: "code",
    username,
    password,
  }
  const response = await ax.post(
    `https://${domain}/api/v1/users/token`,
    objectToQs(payload),
    {headers: {'Content-Type': 'application/x-www-form-urlencoded'}}
  )
  const token = response.data.access_token
  const client = axios.create({
    baseURL: `https://${domain}`,
    headers: {'Authorization': `Bearer ${token}`},
  })
  const accountResponse = await client.get('/api/v1/users/me/')
  const fullUsername = `${accountResponse.data.username}@${domain}`
  store.commit('setRecursiveState', {key: "sources.peertube.appTokens", suffix: fullUsername, value: response.data})
  store.commit('addAccount', {source: 'peertube', raw: accountResponse.data, username: accountResponse.data.username, domain})
  router.push({path: '/'})
}


export default {
  id: "peertube",
  label: "PeerTube",
  url: "https://joinpeertube.org",
  description: "PeerTube servers",
  extendedDescription: "Receive suggestions based on your viewing history",
  imageBackground: "grey lighten-5",
  defaultAvatarIcon: "ondemand_video",
  form: Form,
  connect: true,
  getLogo () {
    return require("../assets/sources/peertube-logo.svg")
  },
  authDataToKey ({username, domain}) {
    return `peertube ${domain} ${username}`
  },
  getAvatar (data) {
    if (!data.raw || !data.raw.account.avatar) {
      return require("../assets/sources/peertube-logo.svg")
    }
    return data.raw.account.avatar.path
  },
  getSuggestionAvatar () {
    return require("../assets/sources/peertube-logo.svg")
  },
  getDisplayName ({username, domain}) {
    return `${username}@${domain}`
  },
  async fetch ({account, store, results, maxDays}) {
    results.status = `Fetching views...`
    const token = store.state.sources.peertube.appTokens[`${account.username}@${account.domain}`].access_token
    const client = getOauthAxios({
      axiosParams: {baseURL: `https://${account.domain}`},
      store: store,
      account: account
    })
    const dateLimit = moment().subtract(maxDays, 'days')
    let url = null
    let handledViews = 0
    // results.progress = 0
    // results.progressCount
    results.accounts = {}
    let cont = true
    let start = 0
    let perPage = 100
    while (cont) {
      let response = await client.get('/api/v1/users/me/history/videos', {params: {start, count: perPage}})
      response.data.data.forEach((v) => {
        let date = moment(v.publishedAt)
        if (date.isBefore(dateLimit)) {
          cont = false
          return
        }

        handledViews += 1
        results.progressCount += 1
        let channel = v.channel
        let channelId = `activitypub:${channel.url}`
        if (results.accounts[channelId]) {
          results.accounts[channelId].weight += 1
          let detail = get(results.accounts[channelId], 'detail', {})
          detail = get(detail, 'peertube', {views: 0})
          detail.views += 1
        } else {
          results.accounts[channelId] = {
            weight: 1,
            source: 'peertube',
            id: channelId,
            avatar: (channel.avatar || {}).path,
            name: channel.displayName,
            url: channel.url,
            detail: {
              peertube: {
                views: 1
              }
            }
          }
        }
        // on PeerTube, both channels and accounts are AP actors
        // and can have descriptions
        let account = v.account
        let accountId = `activitypub:${account.url}`
        if (results.accounts[accountId]) {
          results.accounts[accountId].weight += 1
          let detail = get(results.accounts[accountId], 'detail', {})
          detail = get(detail, 'peertube', {views: 0})
          detail.views += 1
        } else {
          Vue.set(results.accounts, accountId, {
            weight: 1,
            source: 'peertube',
            id: accountId,
            avatar: (account.avatar || {}).path,
            name: account.displayName,
            url: account.url,
            detail: {
              peertube: {
                views: 1
              }
            }
          })
        }
        // results.progress = Math.min(100, handledViews / maxFavorites * 100)
      })
      start += response.data.data.length
      if (start >= response.data.total ) {
        break
      }
      results.status = `Fetched ${handledViews} views`
    }
    results.isLoading = false
    return results
  },
  getDetailMessage (detail) {
    if (!detail) {
      return
    }
    let views = detail.views || 0
    return `peertube: ${views} views`
  }
}
